import { collection, query, where, getDocs, addDoc, serverTimestamp, orderBy, limit } from 'firebase/firestore';
import { db } from './firebase';

// Função para gerar dados de teste
const generateTestData = (modelId) => {
  console.log('Gerando dados de teste para modelId:', modelId);
  const now = new Date();
  const testData = {
    interactions: [],
    completions: []
  };

  // Gerar interações para os últimos 7 dias
  for (let i = 0; i < 7; i++) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    
    // Gerar 5-10 leads por dia (mais realista)
    const leadsPerDay = Math.floor(Math.random() * 6) + 5;
    console.log(`Gerando ${leadsPerDay} leads para o dia ${date.toLocaleDateString()}`);
    
    for (let j = 0; j < leadsPerDay; j++) {
      const leadId = `test-lead-${i}-${j}`;
      // Distribuir interações ao longo do dia (mais realista)
      const interactionTime = new Date(date);
      // Mais interações durante o dia (9h às 21h)
      const hour = Math.floor(Math.random() * 12) + 9;
      interactionTime.setHours(hour);
      interactionTime.setMinutes(Math.floor(Math.random() * 60));
      
      // Adicionar múltiplas interações por lead
      const numInteractions = Math.floor(Math.random() * 3) + 1; // 1-3 interações por lead
      for (let k = 0; k < numInteractions; k++) {
        const interactionTime = new Date(date);
        interactionTime.setHours(hour + k);
        interactionTime.setMinutes(Math.floor(Math.random() * 60));
        
        testData.interactions.push({
          leadId,
          modelId,
          funnelStage: k === numInteractions - 1 ? 'session_completed' : 'initial_contact',
          interaction: k === 0 ? 'page_view' : 'engagement',
          timestamp: interactionTime
        });

        // Se for a última interação e tiver sorte, registrar como finalização
        if (k === numInteractions - 1 && Math.random() > 0.3) { // 70% de chance de finalizar
          const completionTime = new Date(interactionTime);
          completionTime.setMinutes(completionTime.getMinutes() + Math.floor(Math.random() * 30) + 5); // 5-35 minutos depois
          
          testData.completions.push({
            leadId,
            modelId,
            timestamp: completionTime
          });
        }
      }
    }
  }

  // Ordenar interações e finalizações por timestamp
  testData.interactions.sort((a, b) => a.timestamp - b.timestamp);
  testData.completions.sort((a, b) => a.timestamp - b.timestamp);

  console.log('Dados de teste gerados:', {
    totalInteractions: testData.interactions.length,
    totalCompletions: testData.completions.length,
    leadsUnicos: new Set(testData.interactions.map(i => i.leadId)).size
  });

  return testData;
};

export const engagementService = {
  // Obter métricas de engajamento por modelo
  getModelEngagement: async (modelId) => {
    try {
      const engagementsRef = collection(db, 'engagements');
      const q = query(engagementsRef, where('modelId', '==', modelId));
      const querySnapshot = await getDocs(q);
      
      const engagements = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return {
        totalEngagements: engagements.length,
        lastEngagement: engagements[engagements.length - 1]?.timestamp || null
      };
    } catch (error) {
      console.error('Erro ao buscar engajamento do modelo:', error);
      throw error;
    }
  },

  // Registrar interação do lead no funnel
  trackLeadInteraction: async (leadId, modelId, funnelStage, interaction) => {
    try {
      console.log('Registrando interação:', { leadId, modelId, funnelStage, interaction });
      
      const interactionsRef = collection(db, 'leadInteractions');
      await addDoc(interactionsRef, {
        leadId,
        modelId,
        funnelStage,
        interaction,
        timestamp: serverTimestamp()
      });

      // Se for uma finalização, registrar também na coleção de finalizações
      if (funnelStage === 'session_completed') {
        console.log('Registrando finalização para lead:', leadId);
        const completionsRef = collection(db, 'funnelCompletions');
        await addDoc(completionsRef, {
          leadId,
          modelId,
          timestamp: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Erro detalhado ao registrar interação:', error);
      throw error;
    }
  },

  // Obter estatísticas do funnel
  getFunnelStats: async (modelId, timeRange = 'today') => {
    try {
      console.log('Buscando estatísticas para modelId:', modelId);
      
      const interactionsRef = collection(db, 'leadInteractions');
      const completionsRef = collection(db, 'funnelCompletions');
      
      // Definir período de tempo
      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);

      console.log('Período de busca:', { startOfDay, endOfDay });

      try {
        // Buscar interações do dia
        const interactionsQuery = query(
          interactionsRef,
          where('modelId', '==', modelId),
          where('timestamp', '>=', startOfDay),
          where('timestamp', '<', endOfDay)
        );

        console.log('Executando query de interações...');
        const interactionsSnapshot = await getDocs(interactionsQuery);
        console.log('Interações encontradas:', interactionsSnapshot.size);

        // Buscar finalizações do dia
        const completionsQuery = query(
          completionsRef,
          where('modelId', '==', modelId),
          where('timestamp', '>=', startOfDay),
          where('timestamp', '<', endOfDay)
        );

        console.log('Executando query de finalizações...');
        const completionsSnapshot = await getDocs(completionsQuery);
        console.log('Finalizações encontradas:', completionsSnapshot.size);

        let interactions = interactionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        let completions = completionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Se não houver dados reais, retornar dados zerados
        if (interactions.length === 0) {
          console.log('Nenhum dado real encontrado para modelId:', modelId);
          return {
            metrics: {
              leadsToday: 0,
              completionsToday: 0,
              averageTimeInFunnel: 0
            },
            hourlyData: Array.from({ length: 24 }, (_, hour) => ({
              hour: `${hour}:00`,
              leads: 0,
              completions: 0
            }))
          };
        }

        // Calcular métricas
        const uniqueLeadsToday = new Set(interactions.map(i => i.leadId)).size;
        const totalCompletions = completions.length;

        console.log('Métricas calculadas:', { uniqueLeadsToday, totalCompletions });

        // Calcular tempo médio no funil
        const leadTimes = {};
        interactions.forEach(interaction => {
          const leadId = interaction.leadId;
          const timestamp = interaction.timestamp?.toDate ? 
            interaction.timestamp.toDate() : 
            new Date(interaction.timestamp);

          if (!leadTimes[leadId]) {
            leadTimes[leadId] = {
              firstInteraction: timestamp,
              lastInteraction: timestamp
            };
          } else {
            leadTimes[leadId].lastInteraction = timestamp;
          }
        });

        const averageTimeInFunnel = Object.values(leadTimes).reduce((acc, times) => {
          const duration = times.lastInteraction - times.firstInteraction;
          return acc + duration;
        }, 0) / Object.keys(leadTimes).length || 0;

        // Converter para minutos
        const averageTimeInMinutes = Math.round(averageTimeInFunnel / (1000 * 60));

        console.log('Tempo médio calculado:', averageTimeInMinutes, 'minutos');

        // Preparar dados para o gráfico
        const hourlyData = Array.from({ length: 24 }, (_, hour) => ({
          hour: `${hour}:00`,
          leads: 0,
          completions: 0
        }));

        interactions.forEach(interaction => {
          const timestamp = interaction.timestamp?.toDate ? 
            interaction.timestamp.toDate() : 
            new Date(interaction.timestamp);
          const hour = timestamp.getHours();
          hourlyData[hour].leads++;
        });

        completions.forEach(completion => {
          const timestamp = completion.timestamp?.toDate ? 
            completion.timestamp.toDate() : 
            new Date(completion.timestamp);
          const hour = timestamp.getHours();
          hourlyData[hour].completions++;
        });

        console.log('Dados horários preparados:', hourlyData);

        return {
          metrics: {
            leadsToday: uniqueLeadsToday,
            completionsToday: totalCompletions,
            averageTimeInFunnel: averageTimeInMinutes
          },
          hourlyData
        };
      } catch (error) {
        console.error('Erro na query do Firestore:', error);
        // Retornar dados zerados em caso de erro
        return {
          metrics: {
            leadsToday: 0,
            completionsToday: 0,
            averageTimeInFunnel: 0
          },
          hourlyData: Array.from({ length: 24 }, (_, hour) => ({
            hour: `${hour}:00`,
            leads: 0,
            completions: 0
          }))
        };
      }
    } catch (error) {
      console.error('Erro detalhado ao buscar estatísticas do funnel:', error);
      throw error;
    }
  },

  // Obter histórico de engajamento
  getEngagementHistory: async (modelId, days = 7) => {
    try {
      console.log('Buscando histórico para modelId:', modelId, 'dias:', days);
      
      const completionsRef = collection(db, 'funnelCompletions');
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      const endDate = new Date();

      console.log('Período de histórico:', { startDate, endDate });

      try {
        const q = query(
          completionsRef,
          where('modelId', '==', modelId),
          where('timestamp', '>=', startDate),
          where('timestamp', '<=', endDate)
        );

        console.log('Executando query de histórico...');
        const querySnapshot = await getDocs(q);
        console.log('Finalizações encontradas no histórico:', querySnapshot.size);

        let completions = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Se não houver dados reais, retornar histórico zerado
        if (completions.length === 0) {
          console.log('Nenhum dado histórico encontrado para modelId:', modelId);
          return Array.from({ length: days }, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - i);
            return {
              date: date.toLocaleDateString(),
              completions: 0
            };
          });
        }

        // Agrupar por data
        const dailyData = completions.reduce((acc, completion) => {
          const timestamp = completion.timestamp?.toDate ? 
            completion.timestamp.toDate() : 
            new Date(completion.timestamp);
          const date = timestamp.toLocaleDateString();
          if (!acc[date]) {
            acc[date] = 0;
          }
          acc[date]++;
          return acc;
        }, {});

        const history = Object.entries(dailyData).map(([date, count]) => ({
          date,
          completions: count
        }));

        console.log('Histórico preparado:', history);

        return history;
      } catch (error) {
        console.error('Erro na query do histórico:', error);
        // Retornar histórico zerado em caso de erro
        return Array.from({ length: days }, (_, i) => {
          const date = new Date();
          date.setDate(date.getDate() - i);
          return {
            date: date.toLocaleDateString(),
            completions: 0
          };
        });
      }
    } catch (error) {
      console.error('Erro detalhado ao buscar histórico de engajamento:', error);
      throw error;
    }
  },

  // Obter milestones dos leads
  getLeadMilestones: async (modelId) => {
    try {
      const interactionsRef = collection(db, 'leadInteractions');
      const q = query(interactionsRef, where('modelId', '==', modelId));
      const querySnapshot = await getDocs(q);
      
      const interactions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Agrupar interações por lead
      const leadMilestones = interactions.reduce((acc, interaction) => {
        if (!acc[interaction.leadId]) {
          acc[interaction.leadId] = [];
        }
        acc[interaction.leadId].push(interaction);
        return acc;
      }, {});

      // Calcular milestones para cada lead
      return Object.entries(leadMilestones).map(([leadId, interactions]) => {
        const stages = interactions.map(i => i.funnelStage);
        const lastStage = stages[stages.length - 1];
        
        return {
          leadId,
          stage: lastStage,
          completed: lastStage === 'session_completed',
          isCurrent: true,
          timestamp: interactions[interactions.length - 1].timestamp
        };
      });
    } catch (error) {
      console.error('Erro ao buscar milestones:', error);
      throw error;
    }
  }
}; 